import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  ServerStackIcon,
  CircleStackIcon,
  CloudIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  CpuChipIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import {
  adminApi,
  AdminOverview,
  AiUsageBreakdown,
  AiUsageSummary,
  BackupRecord,
} from '../api/admin';
import { signupApi, type SignupIntentAdmin, type SignupStatus } from '../api/signup';
import {
  fleetApi,
  type FleetInstance,
  type ProvisionJob,
  type ProvisionJobStatus,
  type ProvisionReadiness,
  type ProvisionStage,
  type ProvisionTickResult,
  PROVISION_STAGES,
  PROVISION_STAGE_LABELS,
} from '../api/fleet';
import {
  vaultApi,
  type VaultBackupObject,
  type VaultRestoreJob,
  type VaultStatus,
} from '../api/vault';
import { getApiErrorMessage } from '../utils/errorHandling';
import { getTenantSubdomain } from '../config/api';

const formatBytes = (bytes: number | null | undefined): string => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const formatTokens = (tokens: number): string =>
  new Intl.NumberFormat(undefined, { notation: 'compact' }).format(tokens);

const formatUsd = (amount: number): string =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: amount < 1 ? 4 : 2,
    maximumFractionDigits: amount < 1 ? 6 : 2,
  }).format(amount);

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
      }`}
    >
      {ok ? (
        <CheckCircleIcon className="h-3.5 w-3.5" />
      ) : (
        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
      )}
      {label}
    </span>
  );
}

function Card({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
}) {
  return (
    <div className="glass-panel rounded-2xl border border-slate-200/60 p-5">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-5 w-5 text-sky-600" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-900">{value}</span>
    </div>
  );
}

function UsageMeter({
  label,
  usedLabel,
  limitLabel,
  percent,
  ok,
  hint,
}: {
  label: string;
  usedLabel: string;
  limitLabel: string;
  percent: number | null;
  ok: boolean;
  hint?: string;
}) {
  const width = percent === null ? 0 : Math.min(100, Math.max(0, percent));
  return (
    <div className="rounded-xl border border-slate-200/70 bg-white/70 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">
            {usedLabel}
            <span className="font-normal text-slate-500"> / {limitLabel}</span>
          </p>
        </div>
        <StatusPill ok={ok} label={ok ? 'Within plan' : 'Over plan'} />
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all ${
            !ok ? 'bg-rose-500' : width >= 80 ? 'bg-amber-500' : 'bg-sky-500'
          }`}
          style={{ width: percent === null ? '8%' : `${width}%` }}
        />
      </div>
      <div className="mt-2 flex justify-between text-xs text-slate-500">
        <span>{percent === null ? 'Unlimited / fair use' : `${percent.toFixed(0)}% used`}</span>
        {hint ? <span>{hint}</span> : null}
      </div>
    </div>
  );
}

function BreakdownTable({
  title,
  rows,
  empty,
}: {
  title: string;
  rows: AiUsageBreakdown[];
  empty: string;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">{empty}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <h3 className="mb-2 text-sm font-semibold text-slate-700">{title}</h3>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="py-2 pr-4">Name</th>
            <th className="py-2 pr-4 text-right">Requests</th>
            <th className="py-2 pr-4 text-right">Tokens</th>
            <th className="py-2 pr-4 text-right">Avg latency</th>
            <th className="py-2 text-right">Estimated cost</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200/60">
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="py-2 pr-4 font-medium">{row.key}</td>
              <td className="py-2 pr-4 text-right">{row.requestCount.toLocaleString()}</td>
              <td className="py-2 pr-4 text-right">{formatTokens(row.totalTokens)}</td>
              <td className="py-2 pr-4 text-right">{row.averageLatencyMs.toLocaleString()} ms</td>
              <td className="py-2 text-right">{formatUsd(row.estimatedCostUsd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SignupQueueCard() {
  const [rows, setRows] = useState<SignupIntentAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { appUrl: string; instanceId: string; notes: string }>>({});

  const load = useCallback(async () => {
    try {
      const data = await signupApi.adminQueue();
      setRows(data);
      setDrafts((prev) => {
        const next = { ...prev };
        for (const row of data) {
          if (!next[row.id]) {
            next[row.id] = {
              appUrl: row.provisionedAppUrl || '',
              instanceId: row.provisionedInstanceId || '',
              notes: row.opsNotes || '',
            };
          }
        }
        return next;
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load signup queue'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: string, status: SignupStatus) => {
    const draft = drafts[id] || { appUrl: '', instanceId: '', notes: '' };
    setSavingId(id);
    try {
      await signupApi.adminUpdate(id, {
        status,
        opsNotes: draft.notes,
        provisionedAppUrl: draft.appUrl || undefined,
        provisionedInstanceId: draft.instanceId || undefined,
      });
      toast.success(`Signup marked ${status}`);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Update failed'));
    } finally {
      setSavingId(null);
    }
  };

  const pending = rows.filter((r) =>
    ['queued_for_provision', 'awaiting_payment', 'provisioning', 'draft'].includes(r.status),
  );

  return (
    <Card title="Signup queue" icon={ClipboardDocumentListIcon}>
      <p className="mb-3 text-sm text-slate-500">
        Self-serve signups land here. P1 auto-enqueues provision jobs; use this queue for
        Business manual review, rejects, or marking <strong>Live</strong> with a custom URL.
      </p>
      {loading ? (
        <p className="text-sm text-slate-500">Loading queue…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">No signup intents yet.</p>
      ) : (
        <div className="space-y-4">
          {(pending.length ? pending : rows.slice(0, 8)).map((row) => {
            const draft = drafts[row.id] || { appUrl: '', instanceId: '', notes: '' };
            return (
              <div key={row.id} className="rounded-xl border border-slate-200/80 bg-white/80 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-slate-900">
                      {row.orgLegalName}{' '}
                      <span className="font-mono text-xs text-slate-500">{row.subdomain}</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {row.plan} · {row.adminEmail} · {row.status} · pay:{row.paymentStatus}
                    </p>
                  </div>
                  <code className="text-[10px] text-slate-400">{row.id}</code>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <input
                    className="input text-sm"
                    placeholder="Provisioned app URL"
                    value={draft.appUrl}
                    onChange={(e) =>
                      setDrafts((d) => ({
                        ...d,
                        [row.id]: { ...draft, appUrl: e.target.value },
                      }))
                    }
                  />
                  <input
                    className="input text-sm"
                    placeholder="INSTANCE_ID (optional)"
                    value={draft.instanceId}
                    onChange={(e) =>
                      setDrafts((d) => ({
                        ...d,
                        [row.id]: { ...draft, instanceId: e.target.value },
                      }))
                    }
                  />
                </div>
                <textarea
                  className="input mt-2 text-sm"
                  rows={2}
                  placeholder="Ops notes"
                  value={draft.notes}
                  onChange={(e) =>
                    setDrafts((d) => ({
                      ...d,
                      [row.id]: { ...draft, notes: e.target.value },
                    }))
                  }
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary text-xs"
                    disabled={savingId === row.id}
                    onClick={() => update(row.id, 'provisioning')}
                  >
                    Mark provisioning
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary text-xs"
                    disabled={savingId === row.id || !draft.appUrl.trim()}
                    onClick={() => update(row.id, 'live')}
                  >
                    Mark live
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary text-xs"
                    disabled={savingId === row.id}
                    onClick={() => update(row.id, 'rejected')}
                  >
                    Reject
                  </button>
                </div>
              </div>
            );
          })}
          {pending.length === 0 && rows.length > 8 && (
            <p className="text-xs text-slate-400">Showing latest 8 of {rows.length} intents.</p>
          )}
        </div>
      )}
      <button type="button" className="btn btn-secondary mt-3 text-xs" onClick={load}>
        Refresh queue
      </button>
    </Card>
  );
}

function JobStatusBadge({ status }: { status: ProvisionJobStatus }) {
  const styles: Record<ProvisionJobStatus, string> = {
    queued: 'bg-slate-100 text-slate-700',
    running: 'bg-sky-100 text-sky-800',
    awaiting_manual_review: 'bg-amber-100 text-amber-900',
    succeeded: 'bg-emerald-100 text-emerald-800',
    failed: 'bg-rose-100 text-rose-800',
    cancelled: 'bg-slate-200 text-slate-600',
  };
  const labels: Record<ProvisionJobStatus, string> = {
    queued: 'Queued',
    running: 'Running',
    awaiting_manual_review: 'Needs review',
    succeeded: 'Succeeded',
    failed: 'Failed',
    cancelled: 'Cancelled',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${styles[status] || styles.queued}`}
    >
      {labels[status] || status}
    </span>
  );
}

function StageProgress({ job }: { job: ProvisionJob }) {
  const currentIdx = Math.max(0, PROVISION_STAGES.indexOf(job.currentStage as ProvisionStage));
  const done = job.status === 'succeeded';
  const failed = job.status === 'failed';

  return (
    <ol className="mt-3 grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4">
      {PROVISION_STAGES.filter((s) => s !== 'done').map((stage, idx) => {
        let state: 'done' | 'current' | 'pending' | 'error' = 'pending';
        if (done || idx < currentIdx) state = 'done';
        else if (failed && idx === currentIdx) state = 'error';
        else if (idx === currentIdx && job.status === 'running') state = 'current';
        else if (idx === currentIdx && job.status === 'queued') state = 'current';
        else if (idx === currentIdx && job.status === 'awaiting_manual_review') state = 'current';

        const tone =
          state === 'done'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
            : state === 'error'
              ? 'border-rose-200 bg-rose-50 text-rose-800'
              : state === 'current'
                ? 'border-sky-300 bg-sky-50 text-sky-900'
                : 'border-slate-200 bg-slate-50 text-slate-500';

        return (
          <li
            key={stage}
            className={`rounded-lg border px-2 py-1.5 text-[11px] leading-snug ${tone}`}
            title={PROVISION_STAGE_LABELS[stage]}
          >
            <span className="font-semibold">{idx + 1}. </span>
            {PROVISION_STAGE_LABELS[stage] || stage}
          </li>
        );
      })}
    </ol>
  );
}

function ProvisionJobsCard() {
  const customerInstance = Boolean(getTenantSubdomain());
  const [jobs, setJobs] = useState<ProvisionJob[]>([]);
  const [instances, setInstances] = useState<FleetInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [lastTick, setLastTick] = useState<ProvisionTickResult | null>(null);
  const [filter, setFilter] = useState<'active' | 'all'>('active');
  const [readiness, setReadiness] = useState<ProvisionReadiness | null>(null);

  const load = useCallback(async () => {
    try {
      const [j, i, ready] = await Promise.all([
        fleetApi.listJobs(),
        fleetApi.listInstances(),
        fleetApi.getProvisionReadiness().catch(() => null),
      ]);
      setJobs(j);
      setInstances(i);
      if (ready) setReadiness(ready);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load provision jobs'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (customerInstance) {
      setLoading(false);
      return;
    }
    load();
    const t = window.setInterval(load, 15000);
    return () => window.clearInterval(t);
  }, [load, customerInstance]);

  const instanceById = (id: string) => instances.find((row) => row.id === id);

  const retry = async (id: string) => {
    setBusyId(id);
    try {
      await fleetApi.retryJob(id);
      toast.success('Job re-queued — worker will pick it up (or click Run tick)');
      setExpandedId(id);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Retry failed'));
    } finally {
      setBusyId(null);
    }
  };

  const tick = async () => {
    setBusyId('tick');
    try {
      const result = await fleetApi.tickWorker();
      setLastTick(result);
      if (!result.processed) {
        toast(result.reason || 'Nothing to process', { icon: 'ℹ️' });
      } else if (result.statusAfter === 'failed') {
        toast.error(
          `Failed ${result.subdomain || 'job'} at ${result.stageAfter}: ${result.lastError || 'error'}`,
        );
      } else if (result.statusAfter === 'succeeded') {
        toast.success(
          `Provision succeeded for ${result.subdomain || 'instance'} (${result.stagesAdvanced ?? 0} stage(s))`,
        );
      } else {
        toast.success(
          `Advanced ${result.subdomain || 'job'}: ${result.stageBefore} → ${result.stageAfter} (${result.stagesAdvanced ?? 0} step(s))`,
        );
      }
      if (result.jobId) setExpandedId(result.jobId);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Tick failed'));
    } finally {
      setBusyId(null);
    }
  };

  const activeStatuses = ['queued', 'running', 'awaiting_manual_review', 'failed'];
  const active = jobs.filter((j) => activeStatuses.includes(j.status));
  const shown =
    filter === 'active'
      ? active.length
        ? active
        : jobs.slice(0, 12)
      : jobs.slice(0, 20);

  const counts = {
    queued: jobs.filter((j) => j.status === 'queued').length,
    running: jobs.filter((j) => j.status === 'running').length,
    review: jobs.filter((j) => j.status === 'awaiting_manual_review').length,
    failed: jobs.filter((j) => j.status === 'failed').length,
    succeeded: jobs.filter((j) => j.status === 'succeeded').length,
  };

  if (customerInstance || readiness?.controlPlane === false) {
    return (
      <Card title="Provision jobs / fleet" icon={CloudIcon}>
        <p className="text-sm text-slate-600">
          This is your live BirdMan instance. Automated signup provision (Cloudflare, Neon, Fly)
          runs on BirdMan&apos;s control plane, not on this host. Those keys are not missing here
          — they must not be copied onto a customer instance.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Provision jobs / fleet" icon={CloudIcon}>
      <p className="mb-3 text-sm text-slate-500">
        Background worker advances one provision job every ~30s (same logic as{' '}
        <strong>Run tick</strong>). Tick is an ops shortcut: call the worker{' '}
        <em>now</em> instead of waiting for the cron — useful while watching a signup.
        See docs: <code className="text-xs">docs/AUTOMATED_INSTANCE_PROVISION.md</code>.
      </p>

      {readiness && (
        <div
          className={`mb-3 rounded-xl border px-3 py-2 text-xs ${
            readiness.readyForStrict
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-950'
          }`}
        >
          <p className="font-semibold">
            Control-plane mode <code>{readiness.mode}</code>
            {readiness.readyForStrict
              ? ' — keys present for strict automate'
              : ' — missing keys for strict automate (simulated jobs can still succeed in auto)'}
          </p>
          <ul className="mt-1 space-y-0.5">
            {readiness.checks.map((c) => (
              <li key={c.id}>
                {c.ok ? 'OK' : 'Missing'}: {c.detail}
                {c.requiredForStrict ? '' : ' (optional / later gap)'}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-2 text-xs">
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
          Queued {counts.queued}
        </span>
        <span className="rounded-full bg-sky-100 px-2.5 py-1 text-sky-800">
          Running {counts.running}
        </span>
        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-900">
          Review {counts.review}
        </span>
        <span className="rounded-full bg-rose-100 px-2.5 py-1 text-rose-800">
          Failed {counts.failed}
        </span>
        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-800">
          Succeeded {counts.succeeded}
        </span>
      </div>

      {lastTick && (
        <div
          className={`mb-3 rounded-xl border px-3 py-2 text-xs ${
            !lastTick.processed
              ? 'border-slate-200 bg-slate-50 text-slate-600'
              : lastTick.statusAfter === 'failed'
                ? 'border-rose-200 bg-rose-50 text-rose-800'
                : lastTick.statusAfter === 'succeeded'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-sky-200 bg-sky-50 text-sky-900'
          }`}
        >
          <p className="font-semibold">Last Run tick</p>
          {!lastTick.processed ? (
            <p className="mt-0.5">{lastTick.reason}</p>
          ) : (
            <p className="mt-0.5">
              {lastTick.subdomain || lastTick.jobId?.slice(0, 8)} · {lastTick.statusBefore} →{' '}
              {lastTick.statusAfter} · stage {lastTick.stageBefore} → {lastTick.stageAfter} ·{' '}
              {lastTick.stagesAdvanced ?? 0} stage(s) advanced
              {lastTick.lastError ? ` · error: ${lastTick.lastError}` : ''}
            </p>
          )}
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-2">
        <button
          type="button"
          className={`btn text-xs ${filter === 'active' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('active')}
        >
          Active / recent
        </button>
        <button
          type="button"
          className={`btn text-xs ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('all')}
        >
          All jobs
        </button>
        <button type="button" className="btn btn-secondary text-xs" onClick={load}>
          Refresh
        </button>
        <button
          type="button"
          className="btn btn-primary text-xs"
          disabled={busyId === 'tick'}
          onClick={tick}
          title="Immediately runs the same work as the 30-second background worker"
        >
          {busyId === 'tick' ? 'Running…' : 'Run tick'}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading jobs…</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-slate-500">
          No provision jobs yet. Complete a public signup (contracts accepted / paid) to enqueue
          one.
        </p>
      ) : (
        <div className="space-y-3">
          {filter === 'active' && active.length === 0 && jobs.length > 0 && (
            <p className="text-xs text-slate-400">
              No active jobs — showing recent history (including succeeded).
            </p>
          )}
          {shown.map((job) => {
            const fleet = instanceById(job.fleetInstanceId);
            const open = expandedId === job.id;
            const logs = Array.isArray(job.logs) ? job.logs : [];
            return (
              <div key={job.id} className="rounded-xl border border-slate-200/80 bg-white/80 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-slate-900">
                        {fleet?.orgName || 'Instance'}{' '}
                        <span className="font-mono text-xs font-normal text-slate-500">
                          {fleet?.subdomain || '—'}
                        </span>
                      </p>
                      <JobStatusBadge status={job.status} />
                      {fleet?.status && (
                        <span className="text-[11px] text-slate-500">fleet: {fleet.status}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      Stage:{' '}
                      <strong className="text-slate-700">
                        {PROVISION_STAGE_LABELS[job.currentStage] || job.currentStage}
                      </strong>{' '}
                      · attempts {job.attempts}
                      {job.startedAt ? ` · started ${formatDateTime(job.startedAt)}` : ''}
                      {job.finishedAt ? ` · finished ${formatDateTime(job.finishedAt)}` : ''}
                    </p>
                  </div>
                  <code className="text-[10px] text-slate-400">{job.id.slice(0, 8)}</code>
                </div>

                <StageProgress job={job} />

                {job.lastError && (
                  <p className="mt-2 rounded-lg bg-rose-50 px-2 py-1.5 text-xs text-rose-700">
                    {job.lastError}
                  </p>
                )}

                {fleet?.appUrl && (
                  <a
                    href={fleet.appUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-xs text-teal-800 hover:underline"
                  >
                    {fleet.appUrl}
                  </a>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary text-xs"
                    onClick={() => setExpandedId(open ? null : job.id)}
                  >
                    {open ? 'Hide details' : 'Show details'}
                  </button>
                  {['failed', 'awaiting_manual_review', 'cancelled', 'succeeded'].includes(
                    job.status,
                  ) && (
                    <button
                      type="button"
                      className="btn btn-secondary text-xs"
                      disabled={busyId === job.id}
                      onClick={() => retry(job.id)}
                    >
                      {job.status === 'succeeded'
                        ? 'Re-run hosts / DNS'
                        : 'Retry / approve'}
                    </button>
                  )}
                  {fleet &&
                    fleet.status !== 'decommissioned' &&
                    fleet.subdomain !== 'birdman-demo' && (
                    <button
                      type="button"
                      className="btn btn-secondary text-xs text-rose-800"
                      disabled={busyId === `decom-${fleet.id}`}
                      onClick={async () => {
                        const ok = window.confirm(
                          `Decommission ${fleet.subdomain}? This destroys the Fly app, Neon database, and DNS so it stops costing money. This cannot be undone from here.`,
                        );
                        if (!ok) return;
                        setBusyId(`decom-${fleet.id}`);
                        try {
                          const result = await fleetApi.decommissionInstance(
                            fleet.id,
                            'ops_request',
                          );
                          toast.success(
                            `Decommissioned ${fleet.subdomain}: ${result.steps.slice(0, 3).join(' · ')}`,
                          );
                          await load();
                        } catch (err) {
                          toast.error(getApiErrorMessage(err, 'Decommission failed'));
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      {busyId === `decom-${fleet.id}` ? 'Decommissioning…' : 'Decommission'}
                    </button>
                  )}
                </div>

                {open && (
                  <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
                    <div className="grid gap-1 text-[11px] text-slate-600 sm:grid-cols-2">
                      <p>
                        Job ID: <code className="text-slate-800">{job.id}</code>
                      </p>
                      <p>
                        Instance ID:{' '}
                        <code className="text-slate-800">{fleet?.instanceId || '—'}</code>
                      </p>
                      <p>Plan: {fleet?.plan || '—'}</p>
                      <p>Neon: {fleet?.neonProjectId || '—'}</p>
                      <p>Fly app: {fleet?.flyAppName || '—'}</p>
                      <p>API: {fleet?.apiUrl || '—'}</p>
                    </div>
                    <div>
                      <p className="mb-1 text-xs font-semibold text-slate-700">
                        Action log ({logs.length})
                      </p>
                      {logs.length === 0 ? (
                        <p className="text-xs text-slate-400">No log entries yet.</p>
                      ) : (
                        <ul className="max-h-48 space-y-1 overflow-y-auto rounded-lg bg-slate-50 p-2 text-[11px]">
                          {[...logs].reverse().map((entry, idx) => (
                            <li
                              key={`${entry.at}-${idx}`}
                              className={
                                entry.level === 'error'
                                  ? 'text-rose-700'
                                  : entry.level === 'warn'
                                    ? 'text-amber-800'
                                    : 'text-slate-600'
                              }
                            >
                              <span className="text-slate-400">
                                {formatDateTime(entry.at)} · {entry.stage}
                              </span>
                              <br />
                              {entry.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function VaultSafeFaultCard() {
  const [status, setStatus] = useState<VaultStatus | null>(null);
  const [backups, setBackups] = useState<VaultBackupObject[]>([]);
  const [restores, setRestores] = useState<VaultRestoreJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, b, r] = await Promise.all([
        vaultApi.status(),
        vaultApi.listBackups(),
        vaultApi.listRestores(),
      ]);
      setStatus(s);
      setBackups(b);
      setRestores(r);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load vault'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const requestRestore = async (vaultBackupId: string) => {
    setBusy(vaultBackupId);
    try {
      await vaultApi.requestRestore({
        vaultBackupId,
        targetNote: note || 'Restore-to-new-host',
      });
      toast.success('Restore requested — awaiting second admin approval');
      setNote('');
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Request failed'));
    } finally {
      setBusy(null);
    }
  };

  const approve = async (id: string) => {
    setBusy(id);
    try {
      await vaultApi.approveRestore(id);
      toast.success('Second approval recorded');
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Approval failed'));
    } finally {
      setBusy(null);
    }
  };

  const execute = async (id: string) => {
    setBusy(`exec-${id}`);
    try {
      const result = await vaultApi.executeRestore(id);
      toast.success('Runbook materialized — follow steps in job detail');
      await load();
      console.info('Vault restore runbook', result.runbook);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Execute failed'));
    } finally {
      setBusy(null);
    }
  };

  const mirror = async () => {
    setBusy('mirror');
    try {
      const res = await vaultApi.runObjectMirror();
      toast.success(`Object mirror: ${res.mirrored} ok, ${res.failed} failed`);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Mirror failed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card title="Vault / safe-fault (P2–P3)" icon={ShieldCheckIcon}>
      <p className="mb-3 text-sm text-slate-500">
        After each primary backup, BirdMan best-effort replicates into the vault catalog.
        Restore-to-new-host needs <strong>two different admins</strong> (dual control), then
        execute runbook (does not auto-overwrite production).
      </p>
      {loading || !status ? (
        <p className="text-sm text-slate-500">Loading vault…</p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-slate-100 px-2.5 py-1">
              Replication {status.replicationEnabled ? 'on' : 'off'}
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1">
              Local {status.localEnabled ? 'on' : 'off'}
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1">
              Remote ingest {status.remoteIngest ? 'on' : 'off'}
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1">
              Retention {status.retentionDays}d
            </span>
          </div>
          {status.lastReplication.at && (
            <p
              className={`mb-3 text-xs ${
                status.lastReplication.ok ? 'text-emerald-700' : 'text-rose-700'
              }`}
            >
              Last replicate:{' '}
              {status.lastReplication.ok ? 'ok' : 'failed'} ·{' '}
              {status.lastReplication.backupId || '—'} ·{' '}
              {formatDateTime(status.lastReplication.at)}
              {status.lastReplication.error ? ` · ${status.lastReplication.error}` : ''}
            </p>
          )}

          <input
            className="input mb-3 text-sm"
            placeholder="Restore target note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <div className="mb-4 space-y-2">
            <p className="text-xs font-semibold text-slate-700">
              Vault backups ({backups.length})
            </p>
            {backups.length === 0 ? (
              <p className="text-xs text-slate-400">
                None yet — run a database backup with vault replication enabled.
              </p>
            ) : (
              backups.slice(0, 6).map((b) => (
                <div
                  key={b.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200/80 bg-white/80 px-3 py-2 text-xs"
                >
                  <div>
                    <p className="font-semibold text-slate-800">{b.backupId}</p>
                    <p className="text-slate-500">
                      {formatDateTime(b.takenAt)} · {b.storageBackend} · {b.status}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary text-xs"
                    disabled={busy === b.id}
                    onClick={() => requestRestore(b.id)}
                  >
                    Request restore
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="mb-3 space-y-2">
            <p className="text-xs font-semibold text-slate-700">
              Restore jobs ({restores.length})
            </p>
            {restores.slice(0, 6).map((job) => (
              <div
                key={job.id}
                className="rounded-lg border border-slate-200/80 bg-white/80 px-3 py-2 text-xs"
              >
                <p className="font-semibold text-slate-800">
                  {job.sourceBackupId} · {job.status}
                </p>
                <p className="text-slate-500">
                  by {job.requesterEmail}
                  {job.approverEmail ? ` · approved ${job.approverEmail}` : ''}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {job.status === 'awaiting_second_approval' && (
                    <button
                      type="button"
                      className="btn btn-primary text-xs"
                      disabled={busy === job.id}
                      onClick={() => approve(job.id)}
                    >
                      Second approve
                    </button>
                  )}
                  {(job.status === 'approved' || job.status === 'failed') && (
                    <button
                      type="button"
                      className="btn btn-secondary text-xs"
                      disabled={busy === `exec-${job.id}`}
                      onClick={() => execute(job.id)}
                    >
                      Execute runbook
                    </button>
                  )}
                </div>
                {job.runbookSteps?.length > 0 && (
                  <ul className="mt-2 max-h-28 space-y-1 overflow-y-auto text-[11px] text-slate-500">
                    {[...job.runbookSteps].slice(-5).map((s, i) => (
                      <li key={`${s.at}-${i}`}>
                        <strong>{s.step}</strong>: {s.detail}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-secondary text-xs" onClick={load}>
              Refresh
            </button>
            <button
              type="button"
              className="btn btn-secondary text-xs"
              disabled={busy === 'mirror'}
              onClick={mirror}
            >
              Mirror PicGallery/Documents
            </button>
          </div>
        </>
      )}
    </Card>
  );
}

export default function Admin() {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [backups, setBackups] = useState<BackupRecord[]>([]);
  const [aiUsage, setAiUsage] = useState<AiUsageSummary | null>(null);
  const [aiDays, setAiDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ov, bk, usage] = await Promise.all([
        adminApi.getOverview(),
        adminApi.listBackups().catch(() => []),
        adminApi.getAiUsage(aiDays).catch(() => null),
      ]);
      setOverview(ov);
      setBackups(bk);
      setAiUsage(usage);
    } catch {
      toast.error('Failed to load operations overview');
    } finally {
      setLoading(false);
    }
  }, [aiDays]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRunBackup = async () => {
    setRunning(true);
    try {
      await adminApi.runBackup();
      toast.success('Backup completed');
      await load();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Backup failed — check server logs';
      toast.error(message);
    } finally {
      setRunning(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await adminApi.sendTestEmail();
      if (res.sent) {
        toast.success(
          res.simulate ? `Simulate mode: ${res.message}` : `Test email sent to ${res.to}`,
        );
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-slate-500">
        <ArrowPathIcon className="mr-2 h-5 w-5 animate-spin" /> Loading operations…
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
        Unable to load operations data. You may not have admin permissions.
      </div>
    );
  }

  const { instance, storage, observability, mail, backups: backupStatus, subscription } = overview;
  const month = aiUsage?.billingMonth;
  const planCtx = aiUsage?.plan;
  const chartDaily = month?.daily?.length ? month.daily : aiUsage?.daily ?? [];
  const maxDailyCost = Math.max(
    ...chartDaily.map((day) => day.estimatedCostUsd),
    0.000001,
  );
  const maxDailyRequests = Math.max(...chartDaily.map((day) => day.requestCount), 1);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="gradient-title text-2xl font-bold">Operations</h1>
        <p className="text-sm text-slate-500">
          Per-instance identity, storage, observability, AI plan usage, database backups, signup
          queue, and automated provision jobs.
        </p>
      </div>

      <SignupQueueCard />
      <ProvisionJobsCard />
      <VaultSafeFaultCard />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card title="Instance" icon={ServerStackIcon}>
          <Row label="Name" value={instance.name || '—'} />
          <Row
            label="Instance ID"
            value={<code className="text-xs">{instance.id || 'not set'}</code>}
          />
          <Row label="Environment" value={instance.environment} />
          <Row label="Version" value={instance.version} />
          <Row
            label="Identity configured"
            value={<StatusPill ok={instance.configured} label={instance.configured ? 'Yes' : 'Defaults'} />}
          />
        </Card>

        <Card title="Subscription" icon={ShieldCheckIcon}>
          <Row label="Plan" value={subscription.planName} />
          <Row label="Status" value={subscription.status} />
          <Row
            label="Billing configured"
            value={
              <StatusPill
                ok={subscription.billingConfigured}
                label={subscription.billingConfigured ? 'Yes' : 'Fail-open'}
              />
            }
          />
          <Row
            label="Active access"
            value={<StatusPill ok={subscription.hasAccess} label={subscription.hasAccess ? 'Yes' : 'No'} />}
          />
          <Row
            label="AI calls / mo"
            value={
              subscription.limits.aiInvocationsPerMonth === null
                ? 'Unlimited'
                : subscription.limits.aiInvocationsPerMonth.toLocaleString()
            }
          />
          <Row
            label="Storage"
            value={
              subscription.limits.storageGb === null
                ? 'Unlimited'
                : `${subscription.limits.storageGb} GB`
            }
          />
        </Card>

        <Card title="Storage" icon={CloudIcon}>
          <Row
            label="Object storage (R2/S3)"
            value={
              <StatusPill
                ok={storage.objectStorage}
                label={storage.objectStorage ? 'Enabled' : 'Local disk'}
              />
            }
          />
          <Row label="Storage root" value={<code className="text-xs">{storage.storageRoot || 'default'}</code>} />
          <Row
            label="Error tracking (Sentry)"
            value={
              <StatusPill
                ok={observability.sentry}
                label={observability.sentry ? 'Enabled' : 'Off'}
              />
            }
          />
          <Row
            label="Email (SMTP)"
            value={
              <StatusPill
                ok={mail.configured}
                label={
                  mail.simulate
                    ? 'Simulate'
                    : mail.configured
                      ? mail.provider ?? 'Configured'
                      : 'Off'
                }
              />
            }
          />
          {mail.configured && !mail.simulate && mail.host && (
            <Row
              label="SMTP endpoint"
              value={
                <code className="text-xs">
                  {mail.host}:{mail.port}
                </code>
              }
            />
          )}
          <button
            onClick={handleTestEmail}
            disabled={testingEmail}
            className="mt-3 inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <EnvelopeIcon className="h-4 w-4" />
            {testingEmail ? 'Sending…' : 'Send test email'}
          </button>
        </Card>

        <Card title="Database backups" icon={CircleStackIcon}>
          <Row
            label="Scheduled backups"
            value={
              <StatusPill
                ok={backupStatus.enabled}
                label={backupStatus.enabled ? 'Enabled' : 'Disabled'}
              />
            }
          />
          <Row label="Destination" value={backupStatus.destination} />
          <Row label="Schedule" value={<code className="text-xs">{backupStatus.cron}</code>} />
          <Row label="Retention" value={`${backupStatus.retention} copies`} />
          <Row
            label="Last backup"
            value={
              backupStatus.last.at ? (
                <StatusPill
                  ok={backupStatus.last.ok}
                  label={`${formatDateTime(backupStatus.last.at)}`}
                />
              ) : (
                'Never'
              )
            }
          />
          {backupStatus.last.error && (
            <p className="mt-1 text-xs text-rose-600">{backupStatus.last.error}</p>
          )}
          <button
            onClick={handleRunBackup}
            disabled={running || backupStatus.running}
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ArrowPathIcon className={`h-4 w-4 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Running backup…' : 'Run backup now'}
          </button>
        </Card>
      </div>

      <Card title="AI usage and estimated cost" icon={CpuChipIcon}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-500">
            Soft meters follow this instance&apos;s plan tier. Cost is estimated from configured
            per-role provider rates (not your Stripe invoice).
          </p>
          <div className="flex gap-2">
            {[7, 30, 90].map((days) => (
              <button
                key={days}
                onClick={() => setAiDays(days)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  aiDays === days
                    ? 'bg-sky-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {days}d window
              </button>
            ))}
          </div>
        </div>

        {!aiUsage ? (
          <p className="text-sm text-slate-500">
            AI telemetry is unavailable. Run the latest database migration, then make an AI
            request to begin collecting usage.
          </p>
        ) : (
          <div className="space-y-6">
            {planCtx && month && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">
                      {month.label} · {planCtx.name} plan
                    </h3>
                    <p className="text-xs text-slate-500">
                      Billable AI calls exclude exact-cache hits. Indicative budget is the GTM
                      light/med/heavy COGS band for this tier (${planCtx.indicativeAiCostUsdMonthly}
                      /mo), not a hard charge.
                      {!planCtx.billingConfigured
                        ? ' Billing is fail-open on this instance — limits are advisory.'
                        : ''}
                    </p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                    Day {month.daysElapsed}/{month.daysInMonth}
                  </span>
                </div>

                <div className="grid gap-3 lg:grid-cols-2">
                  <UsageMeter
                    label="AI invocations (month)"
                    usedLabel={month.billableInvocations.toLocaleString()}
                    limitLabel={
                      month.invocationLimit === null
                        ? 'Unlimited'
                        : month.invocationLimit.toLocaleString()
                    }
                    percent={month.percentOfInvocationLimit}
                    ok={month.withinInvocationLimit}
                    hint={
                      month.remainingInvocations === null
                        ? undefined
                        : `${month.remainingInvocations.toLocaleString()} left`
                    }
                  />
                  <UsageMeter
                    label="Estimated AI cost vs tier band"
                    usedLabel={formatUsd(month.estimatedCostUsd)}
                    limitLabel={formatUsd(planCtx.indicativeAiCostUsdMonthly)}
                    percent={month.percentOfIndicativeBudget}
                    ok={month.withinIndicativeBudget}
                    hint={`Projected ${formatUsd(month.projectedCostUsd)} / mo`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  {[
                    ['Gateway requests', month.requestCount.toLocaleString()],
                    ['Cache hits', month.cacheHits.toLocaleString()],
                    ['Tokens', formatTokens(month.totalTokens)],
                    [
                      'Projected calls',
                      month.invocationLimit === null
                        ? month.projectedInvocations.toLocaleString()
                        : `${month.projectedInvocations.toLocaleString()} / ${month.invocationLimit.toLocaleString()}`,
                    ],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-xl bg-slate-50 p-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        {label}
                      </p>
                      <p className="mt-1 text-lg font-semibold text-slate-900">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-700">
                Selected window ({aiUsage.period.days} days)
              </h3>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                {[
                  ['Requests', aiUsage.totals.requestCount.toLocaleString()],
                  ['Tokens', formatTokens(aiUsage.totals.totalTokens)],
                  ['Estimated cost', formatUsd(aiUsage.totals.estimatedCostUsd)],
                  ['Cache hits', aiUsage.totals.cacheHits.toLocaleString()],
                  ['Avg latency', `${aiUsage.totals.averageLatencyMs.toLocaleString()} ms`],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl bg-slate-50 p-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      {label}
                    </p>
                    <p className="mt-1 text-lg font-semibold text-slate-900">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {chartDaily.length > 0 && (
              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-700">
                    Daily estimated cost
                    {month?.daily?.length ? ' (billing month)' : ''}
                  </h3>
                  <div className="flex h-32 items-end gap-1 rounded-xl bg-slate-50 p-3">
                    {chartDaily.map((day) => (
                      <div
                        key={`cost-${day.date}`}
                        className="group relative flex min-w-0 flex-1 items-end"
                        title={`${day.date}: ${formatUsd(day.estimatedCostUsd)} · ${day.requestCount} requests`}
                      >
                        <div
                          className="w-full rounded-t bg-sky-500 transition group-hover:bg-sky-600"
                          style={{
                            height: `${Math.max(
                              (day.estimatedCostUsd / maxDailyCost) * 100,
                              day.estimatedCostUsd > 0 ? 4 : 1,
                            )}%`,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-slate-400">
                    <span>{chartDaily[0].date}</span>
                    <span>{chartDaily[chartDaily.length - 1].date}</span>
                  </div>
                </div>
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-700">
                    Daily requests
                    {month?.daily?.length ? ' (billing month)' : ''}
                  </h3>
                  <div className="flex h-32 items-end gap-1 rounded-xl bg-slate-50 p-3">
                    {chartDaily.map((day) => (
                      <div
                        key={`req-${day.date}`}
                        className="group relative flex min-w-0 flex-1 items-end"
                        title={`${day.date}: ${day.requestCount} requests · ${formatTokens(day.totalTokens)} tokens`}
                      >
                        <div
                          className="w-full rounded-t bg-violet-500 transition group-hover:bg-violet-600"
                          style={{
                            height: `${Math.max(
                              (day.requestCount / maxDailyRequests) * 100,
                              day.requestCount > 0 ? 4 : 1,
                            )}%`,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-slate-400">
                    <span>{chartDaily[0].date}</span>
                    <span>{chartDaily[chartDaily.length - 1].date}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-6 lg:grid-cols-2">
              <BreakdownTable
                title={month ? 'Features this month' : 'By feature'}
                rows={month?.byFeature ?? aiUsage.byFeature}
                empty="No feature breakdown yet."
              />
              <BreakdownTable
                title={month ? 'Models this month' : 'By model'}
                rows={month?.byModel ?? aiUsage.byModel}
                empty="No model breakdown yet."
              />
            </div>
          </div>
        )}
      </Card>

      <Card title="Available backups" icon={CircleStackIcon}>
        {backups.length === 0 ? (
          <p className="text-sm text-slate-500">
            No backups yet.{' '}
            {backupStatus.enabled
              ? 'A backup will run on the configured schedule.'
              : 'Enable BACKUP_ENABLED or run one on demand.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 pr-4">Size</th>
                  <th className="py-2">Location</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/60">
                {backups.map((b) => (
                  <tr key={b.name}>
                    <td className="py-2 pr-4 font-mono text-xs">{b.name}</td>
                    <td className="py-2 pr-4">{formatDateTime(b.createdAt)}</td>
                    <td className="py-2 pr-4">{formatBytes(b.sizeBytes)}</td>
                    <td className="py-2">{b.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
